// RREventDaySettings — configure courts, duration, rest, play window.
// RR Update 3 (Feb 2026). All fields save via PATCH /round-robins/{id}.
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, Plus, ClockCountdown, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { roundRobinAPI } from '../../lib/roundRobinApi';

function reportError(err, fallback) {
  const d = err?.response?.data?.detail;
  const msg = typeof d === 'string' ? d : (d?.message || fallback);
  toast.error(msg);
}

// Split by commas / spaces / newlines while preserving the admin's intent.
function tokenize(raw) {
  return String(raw || '')
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function RREventDaySettings({ rr, onChanged, disabled }) {
  const [courtsInput, setCourtsInput] = useState((rr.court_labels || []).join(', '));
  const [duration, setDuration] = useState(rr.match_duration_minutes ?? '');
  const [rest, setRest] = useState(rr.min_rest_minutes ?? 0);
  const [startT, setStartT] = useState((rr.play_day_start_time || '').slice(0, 5));
  const [endT, setEndT] = useState((rr.play_day_end_time || '').slice(0, 5));
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setCourtsInput((rr.court_labels || []).join(', '));
    setDuration(rr.match_duration_minutes ?? '');
    setRest(rr.min_rest_minutes ?? 0);
    setStartT((rr.play_day_start_time || '').slice(0, 5));
    setEndT((rr.play_day_end_time || '').slice(0, 5));
    setDirty(false);
  }, [rr.court_labels, rr.match_duration_minutes, rr.min_rest_minutes, rr.play_day_start_time, rr.play_day_end_time]);

  const cleanedCourts = tokenize(courtsInput);
  const seen = new Set();
  const dedupedCourts = [];
  cleanedCourts.forEach((c) => { if (!seen.has(c)) { seen.add(c); dedupedCourts.push(c); } });

  const removeCourt = (label) => {
    setCourtsInput(dedupedCourts.filter((c) => c !== label).join(', '));
    setDirty(true);
  };
  // Court Preference Order — the list order IS the preference (first = most
  // desirable). Move Up / Move Down reorders it; auto-scheduler assigns the
  // best AVAILABLE court in this order to higher-priority pools first.
  const moveCourt = (index, dir) => {
    const next = [...dedupedCourts];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCourtsInput(next.join(', '));
    setDirty(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await roundRobinAPI.update(rr.id, {
        court_labels: dedupedCourts,
        match_duration_minutes: duration === '' ? null : Number(duration),
        min_rest_minutes: rest === '' ? 0 : Number(rest),
        play_day_start_time: startT || null,
        play_day_end_time: endT || null,
      });
      toast.success('Event day settings saved.');
      setDirty(false);
      onChanged?.();
    } catch (e) { reportError(e, 'Save failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-4" data-testid="rr-event-day-settings">
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#013523] font-semibold">
        <ClockCountdown size={12} weight="duotone" /> Event Day Settings
      </div>
      <div className="text-[11px] text-[#6a766e]">
        Configure the courts you have, how long each match takes, minimum rest between matches, and the daily play window.
        These power the auto-scheduler.
      </div>

      <div className="space-y-2">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Court Preference Order</span>
          <input
            type="text"
            value={courtsInput}
            onChange={(e) => { setCourtsInput(e.target.value); setDirty(true); }}
            disabled={disabled}
            data-testid="rreds-courts-input"
            placeholder="1, 2, 6, 7, 8, 9, 10"
            className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
          />
        </label>
        <div className="text-[11px] text-[#6a766e]">
          List courts from <span className="font-semibold text-[#013523]">most desirable</span> → <span className="font-semibold text-[#013523]">least desirable</span>. The auto-scheduler gives higher-priority pools the best available courts first, in this order.
        </div>
        {dedupedCourts.length > 0 && (
          <div className="flex flex-col gap-1.5" data-testid="rreds-courts-chips">
            {dedupedCourts.map((c, idx) => (
              <div key={c} className="inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-[#f6f4ef] border border-[#e3dcd1] text-[12px] text-[#013523] w-fit" data-testid={`rreds-court-chip-${c}`}>
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#013523] text-white text-[10px] font-semibold tabular-nums" data-testid={`rreds-court-rank-${c}`}>{idx + 1}</span>
                <span className="font-medium">Court {c}</span>
                {!disabled && (
                  <span className="inline-flex items-center gap-0.5">
                    <button type="button" onClick={() => moveCourt(idx, -1)} disabled={idx === 0} data-testid={`rreds-court-up-${c}`} title="Move up (more desirable)" className="text-[#6a766e] hover:text-[#013523] disabled:opacity-30 disabled:cursor-not-allowed">
                      <ArrowUp size={12} weight="bold" />
                    </button>
                    <button type="button" onClick={() => moveCourt(idx, 1)} disabled={idx === dedupedCourts.length - 1} data-testid={`rreds-court-down-${c}`} title="Move down (less desirable)" className="text-[#6a766e] hover:text-[#013523] disabled:opacity-30 disabled:cursor-not-allowed">
                      <ArrowDown size={12} weight="bold" />
                    </button>
                    <button type="button" onClick={() => removeCourt(c)} className="text-[#9aa39d] hover:text-[#013523]" aria-label={`Remove court ${c}`}>
                      <X size={11} weight="bold" />
                    </button>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Match duration (min)</span>
          <input
            type="number" min={5} max={600} step={5}
            value={duration}
            onChange={(e) => { setDuration(e.target.value); setDirty(true); }}
            disabled={disabled}
            data-testid="rreds-duration"
            className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Min rest (min)</span>
          <input
            type="number" min={0} max={600} step={5}
            value={rest}
            onChange={(e) => { setRest(e.target.value); setDirty(true); }}
            disabled={disabled}
            data-testid="rreds-rest"
            className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Play day starts</span>
          <input
            type="time"
            value={startT}
            onChange={(e) => { setStartT(e.target.value); setDirty(true); }}
            disabled={disabled}
            data-testid="rreds-start"
            className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Play day ends</span>
          <input
            type="time"
            value={endT}
            onChange={(e) => { setEndT(e.target.value); setDirty(true); }}
            disabled={disabled}
            data-testid="rreds-end"
            className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
          />
        </label>
      </div>

      {dirty && !disabled && (
        <div className="flex justify-end">
          <button
            onClick={save} disabled={busy}
            data-testid="rreds-save"
            className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs flex items-center gap-1 disabled:opacity-60"
          ><Plus size={12} weight="bold" /> Save settings</button>
        </div>
      )}
    </div>
  );
}
